/**
 * Global fetch override — attaches Bearer token to every request to our API
 * Gateway / Lambda hosts. Callers that already set an Authorization header
 * (e.g. authenticatedFetch) are left alone.
 *
 * NOTE (H21): this monkey-patches window.fetch so third-party scripts loaded
 * on the page inherit the auth token for any match against API_GATEWAY_DOMAINS.
 * Long-term this should be replaced with an explicit createApiClient() helper
 * scoped to our own code.
 */

import { API_GATEWAY_CONFIG } from '../config/apiGatewayConfig';

const originalFetch = window.fetch;

const API_GATEWAY_DOMAINS = [
  'diuh85h4d5.execute-api.us-east-1.amazonaws.com',
  'execute-api.us-east-1.amazonaws.com',
  'lambda-url.us-east-1.on.aws'
];

export const initializeGlobalFetchOverride = () => {
  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
    const isApiRequest = API_GATEWAY_DOMAINS.some((d) => url.includes(d));

    if (!isApiRequest) {
      return originalFetch(input, init);
    }

    const headers = new Headers(init?.headers);
    if (headers.has('Authorization')) {
      // Caller (e.g. authenticatedFetch) already attached credentials.
      return originalFetch(input, { ...init, headers });
    }

    let token: string | null = null;
    if (API_GATEWAY_CONFIG.tokenGetter) {
      try {
        token = await API_GATEWAY_CONFIG.tokenGetter();
      } catch {
        // fall through to storage token
      }
    }
    if (!token) {
      token = sessionStorage.getItem('idToken') || localStorage.getItem('idToken');
    }
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    return originalFetch(input, { ...init, headers });
  };
};

export const restoreOriginalFetch = () => {
  window.fetch = originalFetch;
};
